import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { catchError, combineLatest, filter, forkJoin, of, take } from 'rxjs';
import { Option } from '../../../../../../../../ui-components/core-dropdown/core-dropdown.component';
import {
  ICasePayment,
  INewCasePayment,
} from '../../../../interfaces/case-card-interfaces';
import { select, Store } from '@ngrx/store';
import { AppMessageService } from '../../../../../../../../shared/services/app-message/app-message.service';
import { TranslateService } from '@ngx-translate/core';
import { StaffService } from '../../../../../../../definition/staff/services/staff.service';
import { ParameterService } from '../../../../../../../../shared/services/parameter/parameter.service';
import { CurrencyService } from '../../../../../../../../shared/components/currency/services/currency.service';
import {
  selectDropdownLoaded,
  selectDropdownOptions,
} from '../../../../../../store/selectors/dropdowns.selector';
import Decimal from 'decimal.js';
import { CorePopupComponent } from '../../../../../../../../ui-components/core-popup/core-popup.component';
import { CaseService } from '../../../../../../services/case.services';
import { setPaymentInformations } from '../../../../../../store/actions/service-payment.actions';

@Component({
  selector: 'app-case-payment-form',
  templateUrl: './case-payment-form.component.html',
  styleUrl: './case-payment-form.component.scss',
})
export class CasePaymentFormComponent implements OnInit{
  @ViewChild('formPopup') formPopup: CorePopupComponent;
  @Output() saveOn = new EventEmitter();
  paymentMethods: Option[] = [];
  @Input() suggestedAmount: number = 0;
  payment: INewCasePayment = {
    id: null,
    paymentType: null,
    paymentDate: null,
    institution: null,
    provisionNo: null,
    staff: null,
    exchangeType: null,
    total: null,
    rateOfExchange: null,
    totalTL: null,
  };
  staffs: Option[] = [];
  currencies: Option[] = [];
  showExchangeRate: boolean = false;
  turkishCurrency: string;
  isSubmit: boolean = false;
  institutions: Option[];
  minSelectableDate: Date;
  maxSelectableDate: Date;
  caseId: number | null;
  caseType: number | null;
  isStaffRequired: boolean;
  isProvisionRequired: boolean;
  isInstitutionRequired: boolean;
  isNewPayment: boolean = true;
  constructor(
    private readonly store: Store,
    private readonly messageService: AppMessageService,
    private readonly translate: TranslateService,
    private readonly staffService: StaffService,
    private readonly parameterService: ParameterService,
    private readonly currencyService: CurrencyService,
    private readonly caseService: CaseService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    this.maxSelectableDate = today;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    this.minSelectableDate = sevenDaysAgo;
    forkJoin({
      staffs: this.staffService
        .getStaffDropdown({})
        .pipe(catchError(() => of([]))),
      currencies: this.parameterService
        .getParameterValues('data-group', 'currencies')
        .pipe(catchError(() => of(null))),
      turkishCurrency: this.parameterService
        .getParameterValueByKeyname('turkish-currency-id')
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next:(results) => {
        this.staffs =
          Array.isArray(results.staffs) && results.staffs.length > 0
            ? results.staffs.map((e) => {
                return {
                  value: e?.id,
                  label: e?.firstName + ' ' + e?.lastName,
                  disabled: !e?.isActive,
                };
              })
            : [];

        this.currencies =
          results?.currencies?.datas?.values?.map((data: any) => {
            return {
              label: data?.valueObject?.name?.value,
              value: data?.valueObject?.id?.value,
              disabled: !data?.valueObject?.status?.value,
            };
          }) ?? [];

        this.turkishCurrency = results?.turkishCurrency?.value;
        
        if(this.isNewPayment) {
          this.payment.exchangeType = this.turkishCurrency ? Number(this.turkishCurrency) : null;
        }
      },
      error:(error) => {
        console.error('Error fetching data', error);
      }
    });

    combineLatest([
      this.store.pipe(select(selectDropdownOptions('institutions'))),
      this.store.pipe(select(selectDropdownOptions('paymentMethods'))),
      this.store.pipe(select(selectDropdownLoaded)),
    ])
      .pipe(
        // loaded flag'ı true olduğunda (yani veriler yüklendiğinde) veri akışı devam eder
        filter(([institutions, paymentMethods, loaded]) => loaded),
        take(1)
      )
      .subscribe(([institutions, paymentMethods, loaded]) => {
        this.institutions = institutions;
      });

    this.parameterService
      .getParameterValues('case-parameters', 'payment-methods')
      .subscribe({
        next: (response: any) => {
          this.paymentMethods = response.datas.values.map((data: any) => {
            return {
              label: data.valueObject.name.value,
              value: data.valueObject.id.value,
              provision: data.valueObject.provision.value,
              institution: data.valueObject.institution.value,
              staff: data.valueObject.staff.value,
            };
          });
        },
        error: (err) => {},
      });
  }

  openFormPopup(
    caseId: number | null,
    caseType: number | null,
    caseDate: Date | null,
    payment?: ICasePayment
  ) {
    this.caseId = caseId;
    this.caseType = caseType;
    //update işlemi
    if (payment) {
      this.isNewPayment = false;
      this.payment = {
        id: payment?.id,
        paymentType: payment?.paymentType?.value ?? null,
        paymentDate: payment?.paymentDate
          ? new Date(payment?.paymentDate)
          : null,
        institution: payment?.institution?.value ?? null,
        provisionNo: payment?.provisionNo ?? null,
        staff: payment?.staff?.value ?? null,
        exchangeType: payment?.exchangeType?.value ?? null,
        total: payment?.total ?? null,
        rateOfExchange: payment?.rateOfExchange ?? null,
        totalTL: payment?.totalTL ?? null,
      };
      this.exchangeTypeChange(false);
    } else {
      //create işlemi
      this.isNewPayment = true;
      this.payment = {
        id: null,
        paymentType: null,
        paymentDate: caseDate ? new Date(caseDate) : null,
        institution: null,
        provisionNo: null,
        staff: null,
        exchangeType: this.turkishCurrency
          ? Number(this.turkishCurrency)
          : null,
        total: null,
        rateOfExchange: null,
        totalTL: null,
      };
    }
    this.formPopup.openDialog();
  }

  getCurrencyRate() {
    if (this.showExchangeRate && this.payment.paymentDate) {
      const currencyCode = this.currencies.find(
        (e) => e.value === this.payment.exchangeType
      )?.label;

      if (currencyCode) {
        this.currencyService
          .getRateByDateAndExchangeType(currencyCode, this.payment.paymentDate)
          .subscribe({
            next: (res: any) => {
              if (res?.success) {
                this.payment.rateOfExchange = res?.data?.banknoteBuying ?? null;
              } else {
                this.payment.rateOfExchange = null;
              }
              this.calculateTLPrice();
            },
            error: () => {
              this.payment.rateOfExchange = null;
              this.calculateTLPrice();
            },
          });
      }
    } else {
      this.calculateTLPrice();
    }
  }

  toDecimalSafe(value: number | null): Decimal {
    return new Decimal(value ?? 0); // null ise 0 olarak kabul et
  }

  totalChange() {
    this.calculateTLPrice();
  }

  calculateTLPrice() {
    if(this.payment.total===0){
      this.payment.total=1;
    }
    const total = this.toDecimalSafe(this.payment.total);
    const rate = this.toDecimalSafe(this.payment.rateOfExchange);
    this.payment.totalTL =
      rate.toNumber() === 0 ? total.toNumber() : total.times(rate).toNumber();
  }

  paymentMethodChange() {
    const selected: any = this.paymentMethods.find(
      (m) => m.value === this.payment.paymentType
    );

    if (selected) {
      this.isInstitutionRequired = !!selected.institution;
      this.isProvisionRequired = !!selected.provision;
      this.isStaffRequired = !!selected.staff;
    }

  }

  exchangeTypeChange(calculateRate = true) {
    if (
      this.payment?.exchangeType &&
      this.payment?.exchangeType?.toString() !==
        this.turkishCurrency?.toString()
    ) {
      this.showExchangeRate = true;
    } else {
      this.payment.rateOfExchange = null;

      this.showExchangeRate = false;
    }

    //popup ilk açıldığında kakyıtlı rate değişmemesi için sadece döviz tipi degistiginde cagır
    if (calculateRate) {
      this.getCurrencyRate();
    }
  }

  savePayment() {
    this.isSubmit = true;
    // tarih ödeme tipi döviz tipi eğer döviz tipi tl değilse kur ve tl tutar dolu olmalı
    if (
      !this.payment.paymentDate ||
      !this.payment.paymentType ||
      this.payment.totalTL == undefined ||
      !this.payment?.exchangeType ||
      (this.payment?.exchangeType?.toString() !==
        this.turkishCurrency?.toString() &&
        this.payment.rateOfExchange == undefined) ||
      (this.isInstitutionRequired && !this.payment.institution) ||
      (this.isProvisionRequired && !this.payment.provisionNo) ||
      (this.isStaffRequired && !this.payment.staff)
    ) {
      return;
    }
    this.caseService
      .setCasePayment(this.caseId, this.caseType, this.payment)
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.store.dispatch(
              setPaymentInformations({
                caseDate: res.response.caseDate,
                caseNo: res.response.caseNo,
                caseStatus: res.response.caseStatus,
                caseType: res.response.caseType,
                uid: res.response.uid,
                recordType: res.response.recordType,
                id: res.response.id,
                paymentInformations: res.response.paymentInformations,
                createdAt: res.response.createdAt,
              })
            );
            this.messageService.successToast(
              this.translate.instant('common.success'),
              this.translate.instant(
                this.payment?.id
                  ? 'case.payment-informations.update'
                  : 'case.payment-informations.success'
              )
            );

            this.saveOn.emit(this.payment);
            this.formPopup.closeDialog();
          }
        },
      });
  }

  closePayment() {
    this.showExchangeRate = false;
    this.isSubmit = false;
  }
}
